import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { UsersThree, CheckCircle, MapPin, Stamp, ArrowRight, Path } from "@phosphor-icons/react";
import api from "@/lib/api";

const istTime = (iso) =>
  new Date(iso).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false });

const KPI = ({ label, value, icon: Icon, accent, testId }) => (
  <div className="border border-slate-200 rounded-md p-4 bg-white" data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</p>
      <Icon size={18} weight="duotone" className={accent || "text-slate-400"} />
    </div>
    <p className="font-heading font-black tracking-tight text-4xl font-mono-data">{value}</p>
  </div>
);

export default function Dashboard() {
  const [team, setTeam] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [visits, setVisits] = useState([]);
  const [summary, setSummary] = useState(null);
  const [liveKm, setLiveKm] = useState({});
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const prev = subMonths(new Date(), 1);
    const key = format(prev, "yyyy-MM");
    const cached = localStorage.getItem(`monthly-km-${key}`);
    if (cached) {
      setMonthly(JSON.parse(cached));
      return;
    }
    api.get("/reports/total-km", {
      params: { from_date: format(startOfMonth(prev), "yyyy-MM-dd"), to_date: format(endOfMonth(prev), "yyyy-MM-dd"), format: "json" },
    }).then((r) => {
      const data = { month: format(prev, "MMMM yyyy"), rows: r.data.rows };
      setMonthly(data);
      Object.keys(localStorage).filter((k) => k.startsWith("monthly-km-")).forEach((k) => localStorage.removeItem(k));
      localStorage.setItem(`monthly-km-${key}`, JSON.stringify(data));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.allSettled([
      api.get("/proxy/users"),
      api.get("/proxy/leaves"),
      api.get("/proxy/expenses"),
      api.get("/proxy/visits/all"),
      api.get("/proxy/dashboard/summary"),
    ])
      .then(([t, l, e, v, s]) => {
        if (t.status === "fulfilled") setTeam(t.value.data.filter((u) => u.role === "employee"));
        if (l.status === "fulfilled") setLeaves(l.value.data);
        if (e.status === "fulfilled") setExpenses(e.value.data);
        if (v.status === "fulfilled") setVisits(v.value.data.slice(0, 8));
        if (s.status === "fulfilled") setSummary(s.value.data);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.get("/live-km").then((r) => setLiveKm(r.data)).catch(() => {});
  }, []);

  const present = team.filter((u) => u.attendance);
  const pendingLeaves = leaves.filter((l) => l.status === "pending").length;
  const pendingExpenses = expenses.filter((e) => e.status === "pending").length;
  const today = format(new Date(), "EEEE, dd MMM yyyy");

  if (loading) return <div className="p-8 text-sm text-slate-500" data-testid="dashboard-loading">Loading dashboard…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="dashboard-page">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{today}</p>
      <h1 className="font-heading font-black tracking-tight text-3xl mb-8">Operations Overview</h1>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-8">
        <KPI label="Total Employees" value={team.length} icon={UsersThree} testId="kpi-total-employees" />
        <KPI label="Present Today" value={summary?.present ?? present.length} icon={CheckCircle} accent="text-[#007AFF]" testId="kpi-present-today" />
        <KPI label="KM Today (Live)" value={summary?.total_distance_km != null ? `${summary.total_distance_km}` : "—"} icon={Path} accent="text-[#007AFF]" testId="kpi-km-today" />
        <KPI label="On Field Now" value={team.filter((u) => u.last_location).length} icon={MapPin} accent="text-[#007AFF]" testId="kpi-on-field" />
        <KPI label="Pending Approvals" value={pendingLeaves + pendingExpenses} icon={Stamp} accent={pendingLeaves + pendingExpenses > 0 ? "text-[#FF3B30]" : "text-slate-400"} testId="kpi-pending-approvals" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="border border-slate-200 rounded-md">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Today's Attendance</p>
            <Link to="/attendance" className="text-xs text-[#007AFF] flex items-center gap-1 hover:underline" data-testid="link-attendance">
              View all <ArrowRight size={12} />
            </Link>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {team.slice(0, 8).map((u) => (
                <tr key={u.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-medium">{u.name}</td>
                  <td className="px-4 py-2.5 font-mono-data text-xs text-slate-500">{u.employee_id}</td>
                  <td className="px-4 py-2.5 font-mono-data text-xs">
                    {u.attendance ? istTime(u.attendance.login_time) : "—"}
                  </td>
                  <td className="px-4 py-2.5 font-mono-data text-xs text-slate-500" data-testid={`dashboard-km-${u.employee_id}`}>
                    {u.attendance && (liveKm[u.id] ?? u.attendance.distance_km) != null
                      ? `${liveKm[u.id] ?? u.attendance.distance_km} km`
                      : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-sm uppercase tracking-wider ${u.attendance ? "bg-blue-50 text-[#007AFF]" : "bg-slate-100 text-slate-500"}`}>
                      {u.attendance ? "Present" : "Absent"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="border border-slate-200 rounded-md">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Recent Field Visits</p>
            <Link to="/visits" className="text-xs text-[#007AFF] flex items-center gap-1 hover:underline" data-testid="link-visits">
              View all <ArrowRight size={12} />
            </Link>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {visits.map((v) => (
                <tr key={v.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-medium">{v.shop_name}</td>
                  <td className="px-4 py-2.5 text-slate-500">{v.employee_name}</td>
                  <td className="px-4 py-2.5 font-mono-data text-xs text-slate-500 text-right">{v.date}</td>
                </tr>
              ))}
              {visits.length === 0 && (
                <tr><td className="px-4 py-6 text-slate-400 text-sm">No visits logged yet.</td></tr>
              )}
            </tbody>
          </table>
        </section>
      </div>

      {monthly && (
        <section className="border border-slate-200 rounded-md mt-6" data-testid="monthly-km-section">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Path size={16} weight="duotone" className="text-[#007AFF]" />
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Last Month KM — {monthly.month}</p>
            </div>
            <p className="text-[11px] text-slate-400">Auto-refreshes on the 1st of each month</p>
          </div>
          <table className="w-full text-sm" data-testid="monthly-km-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                {["#", "Employee", "Days Present", "Total KM", "Avg KM/Day", "Hours"].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {monthly.rows.slice(0, 8).map((e, i) => (
                <tr key={e.employee_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`monthly-km-row-${e.employee_id}`}>
                  <td className="px-4 py-2.5 font-mono-data text-xs text-slate-400">{i + 1}</td>
                  <td className="px-4 py-2.5">
                    <span className="font-medium">{e.name}</span>
                    <span className="ml-2 text-xs text-slate-500 font-mono-data">{e.employee_id}</span>
                  </td>
                  <td className="px-4 py-2.5 font-mono-data text-xs">{e.days_present}</td>
                  <td className="px-4 py-2.5 font-mono-data font-semibold">{e.total_km} km</td>
                  <td className="px-4 py-2.5 font-mono-data text-xs">{e.avg_km_per_day} km</td>
                  <td className="px-4 py-2.5 font-mono-data text-xs">{e.total_hours}</td>
                </tr>
              ))}
              {monthly.rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">No activity recorded last month.</td></tr>
              )}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
